import React, { useEffect, useMemo, useState } from "react";
import { Authenticator, useAuthenticator, Flex, Heading, Text, View, TextField, Button } from "@aws-amplify/ui-react";
import { useLocation, useNavigate } from "react-router-dom";
import { signUp } from "aws-amplify/auth"; 
import { setAuthCreateTabAndButtonLabel } from "../auth/authI18n";


export default function LoginPage() {
  const { authStatus, route, toSignUp, isPending } = useAuthenticator((ctx) => [
    ctx.authStatus,
    ctx.route,
    ctx.toSignUp,
    ctx.isPending
  ]);
  const navigate = useNavigate();
  const location = useLocation();

  const [isSigningUp, setIsSigningUp] = useState(false);

  const from = location.state?.from?.pathname || "/devices";

  const inviteFromUrl = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return (params.get("invite") || "").trim();
  }, [location.search]);

  const hasInviteLink = Boolean(inviteFromUrl);

  useEffect(() => {
    if (hasInviteLink && route === "signIn") {
      toSignUp();
    }
  }, [hasInviteLink, route, toSignUp]);

  const [showInviteInput, setShowInviteInput] = useState(false);

  const displayInviteForm = hasInviteLink || showInviteInput;

  useEffect(() => {
    if (authStatus === "authenticated") {
      navigate(from, { replace: true });
    }
  }, [authStatus, from, navigate]);

  
  useEffect(() => {
    setAuthCreateTabAndButtonLabel(displayInviteForm ? "join" : "create");

    // Optional: restore default when leaving login page
    return () => setAuthCreateTabAndButtonLabel("create");
  }, [displayInviteForm]);
  
  const services = useMemo(
    () => ({
      async handleSignUp(formData) {
        setIsSigningUp(true);
        try {
          console.log("handleSignUp formData:", formData);
          console.log("formData companyName:", formData["custom:companyName"]);
          console.log("formData inviteCode:", formData["custom:inviteCode"]);
          
          const { username, password } = formData;

          const baseAttrs =
            formData?.options?.userAttributes
            ?? {};  

          const userAttributes = { ...baseAttrs };

          if (displayInviteForm) {
            const inviteCode = String(
              baseAttrs["custom:inviteCode"] ??
              inviteFromUrl ??
              ""
            ).trim().toUpperCase();

            if (!inviteCode) throw new Error("Please enter your invite code.");
            userAttributes["custom:inviteCode"] = inviteCode;
          } else {
            const company = String(baseAttrs["custom:companyName"] ?? "").trim();

            if (!company) throw new Error("Please enter your company name.");
            if (company.length < 2) throw new Error("Company name must be at least 2 characters.");
            if (company.length > 80) throw new Error("Company name must be 80 characters or fewer.");
          }

            // Optional: log what we're sending during testing (remove after)
            console.log("handleSignUp custom userAttributes:", userAttributes);

            return signUp({
              username,
              password,
              options: {
                userAttributes,
                autoSignIn: { enabled: true },
              },
            });
        } finally {
          setIsSigningUp(false);
        }
      },
    }),
    [displayInviteForm, inviteFromUrl]
  );

  const components = useMemo(
    () => ({
      SignUp: {
        Header() {
          return (
            <View paddingInline="1.5rem" paddingTop="1rem" paddingBottom="0.75rem">
              <Flex direction="column" gap="0.5rem">
                <Heading level={3} margin="0">
                  {displayInviteForm ? "Join an existing organization" : "Create a new organization"}
                </Heading>
                <Text color="font.secondary">
                  {displayInviteForm ? (
                    <>You’re joining an organization via an invite. Create your login to continue.</>
                  ) : (
                    <>
                      Only create a new organization if no one at your company has set up Plex LD yet.
                      <br />
                      If your organization already has an account, ask an administrator to invite you.
                    </>
                  )}
                </Text>
              </Flex>
            </View>
          );
        },

        FormFields() {
          return (
            <>
              <Authenticator.SignUp.FormFields />
              {!displayInviteForm && (
                <View marginTop="0.75rem">
                  <TextField
                    label="Company name"
                    name="custom:companyName"
                    placeholder="e.g., Acme Property Management"
                    isRequired
                  />
                </View>
              )}

              {showInviteInput && (
                <View marginTop="0.75rem">
                  <TextField
                    label="Invite code"
                    name="custom:inviteCode"
                    defaultValue={inviteFromUrl}
                    descriptiveText="Code sent to you by a member of the organization."
                  />
                </View>
              )}
            </>
          );
        },

        Footer() {
          if (hasInviteLink) return null;

          if (showInviteInput) {
            return (
              <View paddingInline="1.5rem" paddingBottom="1rem">
                <Button
                  variation="link"
                  size="small"
                  onClick={() => {
                    setAuthCreateTabAndButtonLabel("create");
                    setShowInviteInput(false);
                  }}
                >
                  Create a new organization instead
                </Button>
                <Text fontSize="small" color="font.secondary">
                  Only do this if no one at your company has set up a Plex LD account yet.
                </Text>
              </View>
            );
          }

          return (
            <View paddingInline="1.5rem" paddingBottom="1rem">
              <Button
                variation="link"
                size="small"
                onClick={() => {
                  setAuthCreateTabAndButtonLabel("join");
                  setShowInviteInput(true);
                }}
              >
                Have an invite code?
              </Button>
            </View>
          );
        },
        PrimaryButton(props) {
          return (
            <Button
              {...props}
              isLoading={isSigningUp}
              isDisabled={isSigningUp}
            />
          );
        },
      },
    }),
    [displayInviteForm, hasInviteLink, showInviteInput, isSigningUp]
  );

  return <Authenticator services={services} components={components} key={displayInviteForm ? "invite" : "create"}/>;
}
